/**
 * Formats d'affichage partagés.
 *
 * Regroupés ici parce que les mêmes conversions servaient déjà dans plusieurs vues : une taille en
 * octets, un âge relatif, une durée d'opération. Module pur, donc testé — ces fonctions sont le
 * genre d'endroit où une erreur d'unité passe inaperçue longtemps.
 */

const nombre = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/**
 * Taille en octets, dans l'unité binaire qui la rend lisible.
 *
 * Unités binaires (Kio, Mio…) et non décimales : c'est ce que rapportent `df` et restic, et
 * afficher des Go là où l'outil dit des Gio introduirait un écart de 7 % sans prévenir.
 */
export function octets(valeur: number | null | undefined): string {
  if (valeur === null || valeur === undefined) return '—';
  if (valeur < 1024) return `${String(valeur)} o`;

  const unités = ['Kio', 'Mio', 'Gio', 'Tio', 'Pio'];
  let taille = valeur / 1024;
  let index = 0;
  while (taille >= 1024 && index < unités.length - 1) {
    taille /= 1024;
    index += 1;
  }
  return `${nombre.format(taille)} ${unités[index]!}`;
}

/** Âge relatif d'un horodatage, du point de vue de maintenant. */
export function ilYA(iso: string | null | undefined, jamais = 'jamais'): string {
  if (iso === null || iso === undefined) return jamais;
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${String(minutes)} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 48) return `il y a ${String(heures)} h`;
  return `il y a ${String(Math.round(heures / 24))} j`;
}

/** Durée d'une opération, ou son état si elle n'est pas terminée. */
export function durée(débutIso: string, finIso: string | null | undefined): string {
  if (finIso === null || finIso === undefined) return 'en cours';
  const secondes = Math.round((new Date(finIso).getTime() - new Date(débutIso).getTime()) / 1000);
  if (secondes < 60) return `${String(secondes)} s`;
  const minutes = Math.floor(secondes / 60);
  const reste = secondes % 60;
  if (minutes < 60)
    return reste === 0 ? `${String(minutes)} min` : `${String(minutes)} min ${String(reste)} s`;
  const heures = Math.floor(minutes / 60);
  return `${String(heures)} h ${String(minutes % 60)} min`;
}

/** Part occupée d'un disque, à partir de l'espace libre et de la taille totale. */
export function occupation(libre: number | null, total: number | null): string {
  if (total === null || total === 0 || libre === null) return '—';
  return `${String(Math.round((1 - libre / total) * 100))} %`;
}
